type JsonLdProps = {
  data: Record<string, unknown> | Record<string, unknown>[];
};

/** Production site origin — keep in sync with canonical URLs and public/llms.txt */
export const SITE_ORIGIN = 'https://certinite.com';

export default function JsonLd({ data }: JsonLdProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_ORIGIN}/#organization`,
  name: 'Certinite',
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/certinite-logo.png`,
  description:
    'Commercial SSL certificates and lifecycle management: single-domain, wildcard and multi-domain SSL, certificate inventory, deployment and renewal across supported Windows, Linux and API targets. Let’s Encrypt automation is also available.',
  sameAs: [
    'https://twitter.com/certinite',
    'https://www.linkedin.com/company/certinite/',
  ],
});

export const generateSoftwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Certinite',
  url: SITE_ORIGIN,
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Security infrastructure, SSL Certificate Management',
  operatingSystem: 'Web, Windows',
  description:
    'An automated, centralized SSL management platform for Windows and IIS. It provides instant ACME and Commercial SSL certificates, automated renewals, and a multi-tenant dashboard.',
  offers: {
    '@type': 'Offer',
    price: '0.00',
    priceCurrency: 'USD',
  },
});

export const generateFAQPageSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateArticleSchema = (
  title: string,
  description: string,
  author: string = 'Certinite Team',
  datePublished?: string,
  pageUrl?: string,
  dateModified?: string,
  schemaType: 'Article' | 'BlogPosting' = 'Article',
  options: { image?: string; language?: string; authorType?: 'Person' | 'Organization'; authorUrl?: string } = {},
) => ({
  '@context': 'https://schema.org',
  '@type': schemaType,
  headline: title,
  description,
  author: {
    '@type': options.authorType || 'Person',
    name: author,
    ...(options.authorUrl ? { url: options.authorUrl } : {}),
  },
  publisher: { '@id': `${SITE_ORIGIN}/#organization` },
  datePublished: datePublished || new Date().toISOString().split('T')[0],
  dateModified: dateModified || datePublished || new Date().toISOString().split('T')[0],
  ...(options.image ? { image: options.image } : {}),
  ...(options.language ? { inLanguage: options.language } : {}),
  ...(pageUrl
    ? {
        url: pageUrl,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${pageUrl}#webpage`,
        },
      }
    : {}),
});

/** Sitewide WebSite node (no SearchAction — there is no public /search route). */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_ORIGIN}/#website`,
  name: 'Certinite',
  url: SITE_ORIGIN,
  description: 'SSL certificate inventory, issuance, deployment and renewal management with Certinite Agent, Gateway and supported API connections.',
  publisher: { '@id': `${SITE_ORIGIN}/#organization` },
});

/** Page-level WebPage for inner routes (pairs with Organization/WebSite from layout). */
export const generateWebPageSchema = (path: string, name: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_ORIGIN}${path}#webpage`,
  url: `${SITE_ORIGIN}${path}`,
  name,
  description,
  isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
});

/** Breadcrumb navigation for rich results and clearer entity hierarchy (GEO-friendly). */
export const generateBreadcrumbListSchema = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_ORIGIN}${item.path}`,
  })),
});

/** Blog index with BlogPosting children for GEO / rich discovery (anchors on /blog#slug). */
export const generateBlogListingSchema = (
  name: string,
  description: string,
  blogPath: string,
  posts: { headline: string; description: string; datePublished: string; slug: string; url?: string }[],
) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${SITE_ORIGIN}${blogPath}#blog`,
  name,
  description,
  url: `${SITE_ORIGIN}${blogPath}`,
  publisher: { '@id': `${SITE_ORIGIN}/#organization` },
  blogPost: posts.map((p) => ({
    '@type': 'BlogPosting',
    '@id': p.url || `${SITE_ORIGIN}${blogPath}#${p.slug}`,
    headline: p.headline,
    description: p.description,
    datePublished: p.datePublished,
    url: p.url || `${SITE_ORIGIN}${blogPath}#${p.slug}`,
  })),
});

/** Summarize key capabilities as a named list for crawlers and answer engines. */
export const generateItemListSchema = (name: string, items: string[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  numberOfItems: items.length,
  itemListElement: items.map((text, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: text,
  })),
});

export const generateGettingStartedHowToSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Get started with Certinite on Windows IIS',
  description:
    'Register in the dashboard, install the Windows Agent on your IIS server, add domains, and let Certinite orchestrate ACME or commercial certificate issuance and IIS binding.',
  totalTime: 'PT30M',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Create a Certinite account',
      text: 'Sign up at certinite.com/register and sign in to the multi-tenant dashboard.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Install the Windows Agent',
      text: 'Download the Certinite Agent package and install the worker service on each Windows IIS server that should receive certificates. The agent uses an outbound-only connection to the central API.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Register your domains',
      text: 'Add domain targets in the dashboard. Certinite orchestrates HTTP-01 ACME validation or commercial CA workflows from the API layer.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Automatic IIS binding',
      text: 'The agent polls for issued .pfx bundles and binds them to the correct IIS site bindings using native Windows administration APIs.',
    },
  ],
});
